import { ref, onScopeDispose } from "vue";
import {
  uploadImage,
  MAX_IMAGE_UPLOAD_SIZE,
  type ImageUploadResponse,
} from "@/services/uploads";
import type { ChatAttachment } from "@/services/ai_agent";

export interface PendingAttachment {
  file: File;
  previewUrl: string;
  uploading: boolean;
  uploaded?: ImageUploadResponse;
  error?: string;
}

const MAX_ATTACHMENTS = 5;
const MAX_SIZE_MB = MAX_IMAGE_UPLOAD_SIZE / 1_000_000;
export const IMAGE_ONLY_FALLBACK_MESSAGE = "What do you see in this image?";

export function usePendingAttachments() {
  const pendingAttachments = ref<PendingAttachment[]>([]);
  const attachmentWarning = ref<string | null>(null);
  const isDragging = ref(false);
  let dragLeaveTimer: ReturnType<typeof setTimeout> | null = null;
  let warningTimer: ReturnType<typeof setTimeout> | null = null;

  const ALLOWED_TYPES = new Set(["image/png", "image/jpeg"]);

  function showWarning(message: string) {
    attachmentWarning.value = message;
    if (warningTimer) clearTimeout(warningTimer);
    warningTimer = setTimeout(() => {
      attachmentWarning.value = null;
    }, 5000);
  }

  function addFiles(files: FileList | File[]) {
    const warnings: string[] = [];
    let skippedType = 0;
    let skippedSize = 0;
    let skippedLimit = 0;

    for (const file of files) {
      if (pendingAttachments.value.length >= MAX_ATTACHMENTS) {
        skippedLimit++;
        continue;
      }
      if (!ALLOWED_TYPES.has(file.type)) {
        skippedType++;
        continue;
      }
      if (file.size > MAX_IMAGE_UPLOAD_SIZE) {
        skippedSize++;
        continue;
      }
      pendingAttachments.value.push({
        file,
        previewUrl: URL.createObjectURL(file),
        uploading: false,
      });
    }

    if (skippedLimit > 0) {
      warnings.push(
        `${skippedLimit} ${skippedLimit === 1 ? "file" : "files"} skipped — maximum is ${MAX_ATTACHMENTS} images`,
      );
    }
    if (skippedType > 0) {
      warnings.push(
        `${skippedType} ${skippedType === 1 ? "file" : "files"} skipped — only PNG and JPG are allowed`,
      );
    }
    if (skippedSize > 0) {
      warnings.push(
        `${skippedSize} ${skippedSize === 1 ? "file" : "files"} skipped — maximum size is ${MAX_SIZE_MB}MB`,
      );
    }

    if (warnings.length > 0) {
      showWarning(warnings.join(". ") + ".");
    }
  }

  function handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    addFiles(input.files);
    input.value = "";
  }

  function handlePaste(event: ClipboardEvent) {
    const items = event.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (const item of items) {
      if (ALLOWED_TYPES.has(item.type)) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      event.preventDefault();
      addFiles(imageFiles);
    }
  }

  function onDragOver() {
    if (dragLeaveTimer) {
      clearTimeout(dragLeaveTimer);
      dragLeaveTimer = null;
    }
    isDragging.value = true;
  }

  function onDragLeave() {
    dragLeaveTimer = setTimeout(() => {
      isDragging.value = false;
    }, 50);
  }

  function onDrop(event: DragEvent) {
    isDragging.value = false;
    const files = event.dataTransfer?.files;
    if (files?.length) {
      addFiles(files);
    }
  }

  function removePendingAttachment(idx: number) {
    const att = pendingAttachments.value[idx];
    URL.revokeObjectURL(att.previewUrl);
    pendingAttachments.value.splice(idx, 1);
  }

  async function uploadPendingAttachments(): Promise<ChatAttachment[] | null> {
    const pending = pendingAttachments.value;

    // Mark all not-yet-uploaded attachments as uploading
    for (const att of pending) {
      if (!att.uploaded) {
        att.uploading = true;
        att.error = undefined;
      }
    }

    // Upload in parallel
    const outcomes = await Promise.allSettled(
      pending.map(async (att) => {
        if (att.uploaded) return att.uploaded;
        const uploaded = await uploadImage(att.file);
        att.uploaded = uploaded;
        return uploaded;
      }),
    );

    // Collect results; mark failures individually
    const result: ChatAttachment[] = [];
    let hasFailure = false;
    for (let i = 0; i < outcomes.length; i++) {
      const outcome = outcomes[i];
      const att = pending[i];
      att.uploading = false;
      if (outcome.status === "fulfilled") {
        result.push({
          url: outcome.value.url,
          key: outcome.value.key,
          contentType: att.file.type,
          filename: att.file.name,
        });
      } else {
        att.error = "Upload failed";
        hasFailure = true;
      }
    }

    if (hasFailure) {
      const failCount = pending.length - result.length;
      showWarning(
        `${failCount} ${failCount === 1 ? "image" : "images"} failed to upload. Remove failed images or try again.`,
      );
      return null;
    }
    return result.length > 0 ? result : null;
  }

  function clearPendingAttachments() {
    for (const att of pendingAttachments.value) {
      URL.revokeObjectURL(att.previewUrl);
    }
    pendingAttachments.value = [];
  }

  onScopeDispose(() => {
    clearPendingAttachments();
    if (warningTimer) clearTimeout(warningTimer);
    if (dragLeaveTimer) clearTimeout(dragLeaveTimer);
  });

  return {
    pendingAttachments,
    attachmentWarning,
    isDragging,
    addFiles,
    handleFileSelect,
    handlePaste,
    onDragOver,
    onDragLeave,
    onDrop,
    removePendingAttachment,
    uploadPendingAttachments,
    clearPendingAttachments,
  };
}
